import { modifyTimeSlotsJSON } from './helper';
import {
  ModiFyTimeSlot,
  ModifySlot,
  TimeSlot,
  SCHEDULE_DAY_ROWS,
  WEEKDAY_NUMBERS,
} from './types';
import { isSlotTimeFilled, normalizeTimePart } from './time-slot-picker-utils';

export const DEFAULT_SLOT_TIMES = {
  start: { hour: '09', minute: '00' },
  end: { hour: '18', minute: '00' },
};

export const createDefaultSlot = (id = 0): ModifySlot => ({
  ...DEFAULT_SLOT_TIMES,
  invalidDataError: false,
  overlapError: false,
  id,
});

/** Empty slot for "+ Add slot" — shows hh : mm placeholders until filled. */
export const createEmptySlot = (id = 0): ModifySlot => ({
  start: { hour: '', minute: '' },
  end: { hour: '', minute: '' },
  invalidDataError: false,
  overlapError: false,
  id,
});

const buildEmptyWeekTimeSlots = (): ModiFyTimeSlot[] =>
  SCHEDULE_DAY_ROWS.map(({ day }) => ({
    day,
    slots: [],
  }));

/** Create mode: Mon–Fri checked with a default 09:00–18:00 slot. */
export const createCreateModeTimeSlots = (): ModiFyTimeSlot[] =>
  buildEmptyWeekTimeSlots().map((timeSlot) => {
    if (WEEKDAY_NUMBERS.includes(timeSlot.day)) {
      return {
        ...timeSlot,
        slots: [createDefaultSlot(0)],
      };
    }
    return timeSlot;
  });

export const generateDefaultScheduleName = (existingNames: string[]): string =>
  `New Schedule ${existingNames?.length + 1}`;

export const parseScheduleTimeSlots = (
  timeSlotsJson: string,
): ModiFyTimeSlot[] => {
  try {
    const parsed = JSON.parse(timeSlotsJson) as ModiFyTimeSlot[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return buildEmptyWeekTimeSlots();
    }
    return modifyTimeSlotsJSON(parsed);
  } catch {
    return buildEmptyWeekTimeSlots();
  }
};

export const isDayActive = (timeSlot: ModiFyTimeSlot): boolean =>
  timeSlot.slots.length > 0;

export const getActiveTimeSlots = (
  timeSlots: ModiFyTimeSlot[],
): ModiFyTimeSlot[] => timeSlots.filter(isDayActive);

const serializeDaySlots = (timeSlot: ModiFyTimeSlot): string =>
  JSON.stringify(
    timeSlot.slots.map((slot) => ({
      start: slot.start,
      end: slot.end,
    })),
  );

/** True when every active day has identical slot times (and implicitly same count). */
export const areActiveDaySlotsInSync = (
  timeSlots: ModiFyTimeSlot[],
): boolean => {
  const activeSlots = getActiveTimeSlots(timeSlots);
  if (activeSlots.length <= 1) {
    return true;
  }

  const reference = serializeDaySlots(activeSlots[0]);
  return activeSlots.every(
    (timeSlot) => serializeDaySlots(timeSlot) === reference,
  );
};

/** All slots on the day have both start and end times filled. */
export const isDaySlotsFilled = (timeSlot: ModiFyTimeSlot): boolean => {
  if (!isDayActive(timeSlot)) {
    return false;
  }
  return timeSlot.slots.every(
    (slot) => isSlotTimeFilled(slot.start) && isSlotTimeFilled(slot.end),
  );
};

/**
 * Show apply icon on the day the user is editing when:
 * 1. At least two active (checked) days exist
 * 2. Active days are not already in sync (same slot count + times on every day)
 * 3. This day is active, fully filled, and is the last focused/edited day
 *
 * Clicking apply copies this day's slot count and times to all other active days
 * (e.g. Tue with 1 slot can be applied to Mon with 2 slots and Wed with 3 slots).
 */
export const shouldShowApplyToActiveDays = (
  timeSlots: ModiFyTimeSlot[],
  day: number,
  lastFocusedDay: number | null,
): boolean => {
  const activeDays = getActiveTimeSlots(timeSlots);
  if (activeDays.length <= 1) {
    return false;
  }

  if (areActiveDaySlotsInSync(timeSlots)) {
    return false;
  }

  if (lastFocusedDay === null || lastFocusedDay !== day) {
    return false;
  }

  const daySlot = timeSlots.find((t) => t.day === day);
  if (!daySlot || !isDayActive(daySlot) || !isDaySlotsFilled(daySlot)) {
    return false;
  }

  return true;
};

/** Copies source day's full slot configuration to all other active days. */
export const applySlotsToAllActiveDays = (
  timeSlots: ModiFyTimeSlot[],
  sourceDay: number,
): ModiFyTimeSlot[] => {
  const source = timeSlots.find((t) => t.day === sourceDay);
  if (!source || !isDayActive(source)) {
    return timeSlots;
  }

  const clonedSlots = source.slots.map((slot, index) => ({
    start: { ...slot.start },
    end: { ...slot.end },
    id: index,
    invalidDataError: false,
    overlapError: false,
  }));

  return timeSlots.map((timeSlot) => {
    if (!isDayActive(timeSlot) || timeSlot.day === sourceDay) {
      return timeSlot;
    }

    return {
      ...timeSlot,
      slots: clonedSlots.map((slot, index) => ({
        ...slot,
        id: index,
      })),
    };
  });
};

export const normalizeTimeSlotsForCompare = (
  timeSlots: ModiFyTimeSlot[],
): string =>
  JSON.stringify(
    timeSlots.map((timeSlot) => ({
      day: timeSlot.day,
      slots: timeSlot.slots.map(({ start, end }) => ({ start, end })),
    })),
  );

export const stripSlotsForPayload = (timeSlots: ModiFyTimeSlot[]): TimeSlot[] =>
  timeSlots.map((timeSlot) => ({
    day: timeSlot.day,
    slots: timeSlot.slots.map((slot) => {
      const slotWithoutMeta = { ...slot };
      delete slotWithoutMeta.overlapError;
      delete slotWithoutMeta.id;
      delete slotWithoutMeta.invalidDataError;
      return {
        start: {
          hour: normalizeTimePart(slotWithoutMeta?.start?.hour),
          minute: normalizeTimePart(slotWithoutMeta?.start?.minute),
        },
        end: {
          hour: normalizeTimePart(slotWithoutMeta?.end?.hour),
          minute: normalizeTimePart(slotWithoutMeta?.end?.minute),
        },
      };
    }),
  }));

export const hasSlotValidationErrors = (timeSlots: ModiFyTimeSlot[]): boolean =>
  timeSlots.some((timeSlot) =>
    timeSlot.slots.some((slot) => slot.invalidDataError || slot.overlapError),
  );

export const getTimeSlotByDay = (
  timeSlots: ModiFyTimeSlot[],
  day: number,
): ModiFyTimeSlot | undefined => timeSlots.find((t) => t.day === day);
